import { useTranslation } from "react-i18next";
import { getCookie } from "typescript-cookie";
import { Modal } from "antd";
import { useDeleteStorageMutation } from "../../../../redux/api/organic/storageApi";
import { EntStorageRegister } from "../../../../models/organic/storage";
import { mode, setUrlSource } from "../../../../utils";

const useStorage = (modal: any, refresh?: () => void) => {
  const { t } = useTranslation();
  const { confirm } = modal;
  const [deleteStorage] = useDeleteStorageMutation();

  const showError = (msg: string) =>
	Modal.error({ title: t("msg.error"), content: msg });

  const showSuccess = (msg: string) =>
	Modal.success({ title: t("msg.success"), content: msg });

  const buildFormData = (data: EntStorageRegister): FormData => {
	const formData = new FormData();
	const { files, deleteFileIds, ...rest } = data;

	formData.append(
	  "data",
	  new Blob(
		[JSON.stringify({ ...rest, deleteFileIds })],
		{ type: "application/json" }
	  )
	);

	if (files?.length) {
	  files.forEach((file: any) => {
		const fileToAdd = file instanceof File ? file : file.originFileObj;
		if (fileToAdd) {
		  formData.append("files", fileToAdd);
		}
	  });
	}

	return formData;
  };

  const handleResponse = async (response: Response): Promise<any> => {
	if (!response.ok) {
	  const errorText = await response.text();
	  throw new Error(errorText || t("unknownError"));
	}
	const result = await response.json();
	if (result.error) {
	  const lang = getCookie("i18next") === "ky" ? "kg" : "ru";
	  const errorMessage =
		result.error?.data?.res?.[lang] ||
		result.error?.data?.res?.ru ||
		result.error?.data?.msg ||
		t("unknownError");
	  throw new Error(errorMessage);
	}
	return result;
  };

  async function onAddStorage(data: EntStorageRegister): Promise<boolean> {
	try {
	  const formData = buildFormData(data);
	  const response = await fetch(`${ setUrlSource(mode) }departments/organic/storage-registers`, {
		method: "POST",
		body: formData,
		headers: {
		  Authorization: `Bearer ${ window.localStorage.getItem("token") }`,
		},
	  });
	  const result = await handleResponse(response);

	  if (result.success) {
		showSuccess(t("msg.successAdd"));
		refresh?.();
		return true;
	  }
	  throw new Error(t("unknownError"));
	} catch (error) {
	  showError(error instanceof Error ? error.message : String(error));
	  return false;
	}
  }

  async function onEditStorage(data: EntStorageRegister): Promise<boolean> {
	try {
	  const formData = buildFormData(data);
	  const response = await fetch(`${ setUrlSource(mode) }departments/organic/storage-registers`, {
		method: "PUT",
		body: formData,
		headers: {
		  Authorization: `Bearer ${ window.localStorage.getItem("token") }`,
		},
	  });
	  const result = await handleResponse(response);

	  if (result.success) {
		showSuccess(t("msg.successEdited"));
		refresh?.();
		return true;
	  }
	  throw new Error(t("unknownError"));
	} catch (error) {
	  showError(error instanceof Error ? error.message : String(error));
	  return false;
	}
  }

  async function onDeleteStorage(storage: EntStorageRegister): Promise<boolean> {
	return new Promise((resolve) => {
	  confirm({
		centered: true,
		title: t("msg.confirmDeleteText"),
		okText: t("general.yes"),
		cancelText: t("general.no"),
		onOk: async () => {
		  try {
			const response = await deleteStorage(storage.id as number).unwrap();
			if (response.success) {
			  showSuccess(t("msg.successDelete"));
			  refresh?.();
			  resolve(true);
			} else {
			  throw new Error(t("unknownError"));
			}
		  } catch (error: any) {
			showError(error?.data?.msg || t("unknownError"));
			resolve(false);
		  }
		},
		onCancel: () => resolve(false),
	  });
	});
  }

  return { onAddStorage, onEditStorage, onDeleteStorage };
};

export { useStorage };
